import HomeHero from "../components/HomeHero";
import ClientLogoMarquee from "../components/ClientLogoMarquee";
import React from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  Phone,
  Hammer,
  Zap,
  Sparkles,
  Star,
  ArrowRight,
} from "lucide-react";
import {
  STATS,
  PORTFOLIO,
  COMPANY,
  SERVICE_CATEGORIES,
  FAQS,
} from "../content";
import { FEATURED_ONGOING } from "../data/ongoingProjects";
import usePageMeta from "../hooks/usePageMeta";
import useHashScroll from "../hooks/useHashScroll";
import useHomeMotion from "../hooks/useHomeMotion";
import { track } from "../lib/analytics";
import FAQSection from "../components/FAQSection";
import TrustSection from "../components/TrustSection";

const ICONS = { Hammer, Zap, Sparkles };

export default function Home() {
  const homeMotionRef = useHomeMotion();
  useHashScroll();
  usePageMeta({
    title: "Interior Fit-Out Company Dubai | Time Furniture",
    description:
      "Turnkey fit-out, renovation, joinery and custom furniture across Dubai and the UAE. Established 1993. Book a site visit.",
    canonical: "https://timefurniture.ae/",
    ogImage: "/media/turnkey-revision-oct5/time-furniture-dubai-full-turnkey-renovation-20261005.webp",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });
  return (
    <div ref={homeMotionRef} data-testid="home-page" className="bg-[#FAF5EC]">
      {/* ========== 1 · HERO ========== */}
      <HomeHero />

      {/* ========== HERO MICRO CTA ========== */}
      <section
        data-testid="hero-micro-cta"
        className="bg-[#FAF5EC] border-b border-[#6E1F2C]/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 md:py-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 text-center">
          <p className="text-[#3E2A22] text-sm md:text-[15px] leading-relaxed">
            Looking for fit-out or renovation?
          </p>
          <a
            href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappFitOutText}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-micro-cta-whatsapp"
            className="inline-flex items-center gap-2 text-[#6E1F2C] text-[11px] tracking-[0.32em] uppercase border-b border-[#6E1F2C]/40 hover:border-[#6E1F2C] pb-1 transition-colors"
          >
            <MessageCircle size={14} strokeWidth={2.2} /> Speak to our team on WhatsApp
          </a>
        </div>
      </section>

      {/* ========== QUICK SERVICE LINKS — Curtains & Holiday Homes ========== */}
      <section
        data-testid="home-quick-services"
        className="bg-[#FAF5EC] border-b border-[#6E1F2C]/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 md:py-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              to: "/curtains-dubai",
              testid: "home-card-curtains",
              kicker: "Curtains & Blinds",
              title: "Custom curtains, blinds & motorised systems",
              copy: "Measured, stitched and installed by our own team — single windows to full homes.",
            },
            {
              to: "/holiday-homes",
              testid: "home-card-holiday",
              kicker: "Holiday-Home Furnishing",
              title: "Rental-ready holiday home packages",
              copy: "DTCM-compliant turnkey furnishing for studios, apartments and villas.",
            },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              className="group bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C] p-6 md:p-7 flex items-center justify-between gap-6 transition-colors"
            >
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">{c.kicker}</p>
                <p className="font-serif-display text-xl md:text-2xl text-[#1C110C] leading-snug">{c.title}</p>
                <p className="mt-2 text-sm text-[#7A6B5F] leading-relaxed">{c.copy}</p>
              </div>
              <span className="shrink-0 text-[#6E1F2C] group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ========== TRUST STRIP ========== */}
      <section
        data-testid="trust-strip"
        className="bg-[#FAF5EC] border-b border-[#6E1F2C]/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 md:py-10 flex flex-col items-center text-center gap-4">
          <p className="font-serif-display text-base md:text-lg text-[#1C110C] tracking-tight">
            Serving clients across <em className="not-italic text-[#6E1F2C]">Dubai, Abu Dhabi, Sharjah</em>, and all Emirates
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[#7A6B5F]">
            <span data-testid="trust-experience" className="text-[#6E1F2C]">Established 1993</span>
            <span aria-hidden="true" className="text-[#D4A963]">·</span>
            <span data-testid="trust-team">In-House Team</span>
            <span aria-hidden="true" className="text-[#D4A963]">·</span>
            <span data-testid="trust-execution">End-to-End Execution</span>
          </div>
        </div>
      </section>

      {/* ========== TRADE STRIP ========== */}
      <section className="border-y border-[#6E1F2C]/15 bg-[#F2E9D8] overflow-hidden">
        <div className="flex marquee whitespace-nowrap py-5 text-[11px] tracking-[0.4em] uppercase text-[#6E1F2C]">
          {Array.from({ length: 2 }).map((_, idx) => (
            <div key={idx} className="flex shrink-0 gap-12 px-6">
              {["Turnkey Fit-Out","·","Civil & Renovation","·","Demolition","·","MEP","·","Joinery & Furniture","·","Upholstery","·","Curtains & Soft","·","Flooring","·","Interior Design","·","Lighting & Automation","·"].map((w, i) => (
                <span key={i}>{w}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ========== 2 · ABOUT ========== */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44 lg:py-52">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 overflow-hidden img-zoom-parent">
            <img loading="lazy"
              src="/media/revision-oct5/time-furniture-woodworking-craftsmanship.webp"
              alt="Woodworking craftsmanship — hand-finishing a timber detail in black and white"
              className="w-full h-[520px] object-cover img-zoom"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">About</p>
            <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1C110C]">
              Interior Fit-Out <em className="text-[#6E1F2C] not-italic">Contractor</em> in Dubai, Abu Dhabi &amp; Sharjah.
            </h2>
            <p className="mt-10 text-[#3E2A22] leading-relaxed text-base md:text-lg max-w-2xl">
              We design and deliver complete interior fit-out solutions across the UAE — from concept and planning to full on-site execution. Since 1993, <strong className="font-medium">Time Furniture Turnkey Projects Contracting LLC</strong> has been a turnkey contracting company handling complete interior projects including civil work, MEP, joinery, custom furniture, and final finishing — all under one roof.
            </p>
            <p className="mt-5 text-sm md:text-base text-[#3E2A22] max-w-2xl">
              We work with homeowners, villa owners, offices, healthcare facilities, and commercial clients across the UAE.
            </p>
            <p className="mt-5 text-sm md:text-base text-[#7A6B5F] max-w-2xl">
              <Link to="/services" className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]">
                Explore our turnkey fit-out services
              </Link>{" "}
              or{" "}
              <Link to="/portfolio" className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]">
                view completed interior fit-out projects
              </Link>
              .
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-14 border-t border-[#6E1F2C]/15 pt-12">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="font-serif-display text-4xl md:text-5xl text-[#6E1F2C]">{s.value}</p>
                  <p className="mt-3 text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== 3 · SERVICES (GROUPED) ========== */}
      <section className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44 lg:py-52">
          <div className="text-center max-w-4xl mx-auto mb-20 md:mb-28">
            <p className="eyebrow mb-6">What We Deliver</p>
            <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[#1C110C]">
              Complete Turnkey <em className="text-[#6E1F2C] not-italic">Interior Solutions</em>
            </h2>
            <p className="mt-8 text-[#3E2A22] leading-relaxed text-lg">
              Three disciplines. One team. One contract.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {SERVICE_CATEGORIES.map((cat, i) => {
              const Icon = ICONS[cat.iconName];
              return (
                <Link
                  key={cat.key}
                  to={`/services#${cat.key}`}
                  data-testid={`service-category-${cat.key}`}
                  className="group relative block overflow-hidden bg-[#1C110C] h-[520px] md:h-[560px] lg:h-[600px]"
                >
                  <img
                    src={cat.image}
                    alt={cat.imageAlt || cat.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-all duration-700 group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-[#1C110C]/40 via-[#1C110C]/70 to-[#1C110C]/95 group-hover:from-[#1C110C]/30 group-hover:via-[#1C110C]/55 group-hover:to-[#1C110C]/90 transition-all duration-700" />

                  <div className="relative h-full p-10 md:p-12 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <span className="inline-flex items-center justify-center w-14 h-14 bg-[#6E1F2C] text-white">
                        <Icon size={22} strokeWidth={1.6} />
                      </span>
                      <span className="font-serif-display text-3xl text-[#D4A963]">0{i + 1}</span>
                    </div>

                    <div>
                      <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-4">
                        {cat.count} Services
                      </p>
                      <h3 className="font-serif-display text-4xl md:text-5xl text-white leading-[1.05] mb-5">
                        {cat.title}
                      </h3>
                      <p className="text-white/85 leading-relaxed text-base md:text-lg max-w-[380px]">
                        {cat.oneLine}
                      </p>
                      <span className="mt-8 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-white translate-y-2 group-hover:translate-y-0 opacity-80 group-hover:opacity-100 transition-all duration-500">
                        Learn More <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-16 md:mt-20 text-center">
            <Link
              to="/services"
              data-testid="home-cta-services"
              className="inline-flex items-center gap-3 border border-[#6E1F2C] text-[#6E1F2C] px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-all"
            >
              Explore All Interior Fit-Out Services
            </Link>
          </div>
        </div>
      </section>

      {/* ========== 4 · CLIENT LOGOS ========== */}
      <section className="py-28 md:py-36 lg:py-44 bg-[#FAF5EC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center mb-16 md:mb-20">
          <p className="eyebrow mb-6">Partners & Clients</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1C110C]">
            Trusted by <em className="text-[#6E1F2C] not-italic">Leading Brands</em>
          </h2>
        </div>

        <ClientLogoMarquee />
      </section>

      {/* ========== 3.75 · TRUSTED BY CLIENTS ACROSS UAE — proof metrics ========== */}
      <section
        data-testid="home-credibility"
        className="bg-[#1C110C] text-[#FAF5EC] border-y border-[#D4A963]/15"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <p className="eyebrow text-[#D4A963] mb-5">Trusted Since 1993</p>
            <h2
              data-testid="home-credibility-heading"
              className="font-serif-display text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#FAF5EC]"
            >
              Trusted by clients across the{" "}
              <em className="text-[#D4A963] not-italic">UAE</em>.
            </h2>
            <p className="mt-6 text-[#EDE1C8]/80 leading-[1.85] text-base md:text-[17px]">
              Three decades of single-contract execution — from private villas
              and city apartments to corporate offices and hospitality lounges.
            </p>
          </div>

          <div
            data-testid="home-credibility-stats"
            className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6 md:gap-x-10 border-t border-[#D4A963]/20 pt-14 md:pt-16"
          >
            {[
              {
                value: "1993",
                label: "Established",
                sub: "UAE interior fit-out contractor",
              },
              {
                value: "2,300+",
                label: "Projects delivered",
                sub: "Residential · Commercial · Hospitality",
              },
              {
                value: "7",
                label: "Emirates served",
                sub: "Dubai · Abu Dhabi · Sharjah · UAE-wide",
              },
              {
                value: "100%",
                label: "In-house execution",
                sub: "Joinery · Upholstery · Curtains",
              },
            ].map((s, i) => (
              <div
                key={s.label}
                data-testid={`home-credibility-stat-${i}`}
                className="text-center md:text-left"
              >
                <p className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-[#D4A963] leading-[0.95] tracking-tight">
                  {s.value}
                </p>
                <p className="mt-4 text-base md:text-lg text-[#FAF5EC] leading-snug">
                  {s.label}
                </p>
                <p className="mt-2 text-[10px] md:text-[11px] tracking-[0.24em] uppercase text-[#EDE1C8]/55">
                  {s.sub}
                </p>
              </div>
            ))}
          </div>

          {/* Simple credibility lines */}
          <ul
            data-testid="home-credibility-lines"
            className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-4 text-center md:text-left text-[#EDE1C8]/85 text-sm md:text-[15px]"
          >
            {[
              "Single-contract turnkey delivery — one team, one accountable lead.",
              "In-house atelier in Sharjah — no third-party supply risk.",
              "Authority approvals handled — DM, Trakheesi, DCD, JAFZA, DMCC, DIFC.",
            ].map((line) => (
              <li
                key={line}
                className="flex items-baseline gap-3 leading-[1.7] justify-center md:justify-start"
              >
                <span aria-hidden="true" className="text-[#D4A963] text-xs translate-y-[1px]">
                  ◦
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ========== 4 · WHY CHOOSE US ========== */}
      <TrustSection
        testid="home-trust"
        heading="Why clients choose Time Furniture."
        subtone={true}
      />

      {/* ========== 4.5 · HOW WE DELIVER TURNKEY PROJECTS ========== */}
      <section
        data-testid="home-process"
        className="bg-[#FAF5EC] border-t border-[#6E1F2C]/15"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl mb-16 md:mb-20">
            <p className="eyebrow mb-5">Our Process</p>
            <h2
              data-testid="home-process-heading"
              className="font-serif-display text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[1.04] text-[#1C110C]"
            >
              How we deliver{" "}
              <em className="text-[#6E1F2C] not-italic">turnkey projects</em>.
            </h2>
            <p className="mt-6 text-[#3E2A22] leading-[1.85] text-base md:text-[17px]">
              One contract, one project lead, one accountable team — from first
              site visit to the day you walk into a fully completed space.
            </p>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12 md:gap-x-10 lg:gap-x-6">
            {[
              {
                n: "01",
                title: "Consultation & Site Visit",
                body: "We meet you on site, take measurements, understand your brief and identify any structural, MEP or approval requirements up front.",
              },
              {
                n: "02",
                title: "Design & Scope Finalization",
                body: "Concept layouts, finish boards, FF&E selections and a transparent itemised BOQ — so the budget and timeline are clear before signing.",
              },
              {
                n: "03",
                title: "Execution: Civil + MEP + Joinery",
                body: "Demolition, civil works, MEP first-fix and bespoke joinery installation — coordinated by a single project lead, not a chain of subcontractors.",
              },
              {
                n: "04",
                title: "Finishing & Installation",
                body: "Marble, lighting, second-fix, custom furniture, upholstery, curtains and final styling — every surface installed to handover-photography standard.",
              },
              {
                n: "05",
                title: "Handover",
                body: "Final snag, deep clean, walkthrough and a documented defects-warranty period — we stay accountable after handover, not just before.",
              },
            ].map((s) => (
              <li
                key={s.n}
                data-testid={`home-process-step-${s.n}`}
                className="border-t border-[#6E1F2C]/20 pt-6"
              >
                <p className="font-serif-display text-2xl md:text-3xl text-[#D4A963] mb-4 leading-none">
                  {s.n}
                </p>
                <h3 className="font-serif-display text-lg md:text-xl text-[#1C110C] mb-3 leading-snug">
                  {s.title}
                </h3>
                <p className="text-[#3E2A22] leading-[1.7] text-sm md:text-[15px]">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ========== 5 · PORTFOLIO (MAIN HIGHLIGHT) ========== */}
      <section id="portfolio" className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15 scroll-mt-24">
        <div className="max-w-[1720px] mx-auto px-6 md:px-12 py-28 md:py-44 lg:py-52">
          <div className="text-center max-w-4xl mx-auto mb-20 md:mb-28">
            <p className="eyebrow mb-6">Selected Work</p>
            <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl tracking-tight text-[#1C110C] leading-[1.02]">
              Projects, <em className="text-[#6E1F2C] not-italic">delivered end-to-end</em>.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
            {PORTFOLIO.slice(0, 4).map((p) => (
              <Link
                key={p.id}
                to={`/portfolio/${p.id}`}
                data-testid={`home-portfolio-${p.id}`}
                className="group relative overflow-hidden block h-[560px] md:h-[720px] lg:h-[820px] bg-[#1C110C]"
              >
                <img loading="lazy"
                  src={p.image}
                  alt={p.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.12]"
                />

                {/* Permanent bottom shade so category remains legible */}
                <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

                {/* Hover dark overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/65 transition-colors duration-700 ease-out" />

                {/* Category pill (always visible top-left) */}
                <div className="absolute top-8 left-8">
                  <span className="inline-block bg-[#1C110C]/70 backdrop-blur-sm text-[#D4A963] text-[10px] tracking-[0.32em] uppercase px-4 py-2 border border-white/10">
                    {p.category} · {p.year}
                  </span>
                </div>

                {/* Title — subtle at rest, dominant on hover */}
                <div className="absolute inset-x-0 bottom-0 p-8 md:p-12 transition-all duration-700 ease-out">
                  <div className="opacity-100 group-hover:opacity-0 group-hover:-translate-y-4 transition-all duration-500">
                    <p className="font-serif-display text-2xl md:text-3xl text-white leading-tight">{p.title}</p>
                  </div>
                </div>

                {/* Centered hover content */}
                <div className="absolute inset-0 flex items-center justify-center px-8 md:px-12 opacity-0 group-hover:opacity-100 scale-95 group-hover:scale-100 transition-all duration-700 ease-out">
                  <div className="text-center">
                    <p className="font-serif-display text-4xl md:text-6xl lg:text-7xl text-white leading-[1.05] max-w-3xl mx-auto">
                      {p.title}
                    </p>
                    <span className="mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.36em] uppercase text-[#D4A963] border-b border-[#D4A963] pb-2">
                      View Project <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 md:mt-20 text-center">
            <Link
              to="/portfolio"
              data-testid="home-cta-portfolio"
              className="inline-flex items-center gap-3 border border-[#6E1F2C] text-[#6E1F2C] px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-all"
            >
              View All Interior Fit-Out Projects
            </Link>
          </div>
        </div>
      </section>

      {/* ========== 6b · ONGOING PROJECTS (compact teaser) ========== */}
      <section data-testid="home-ongoing-projects" className="bg-[#FAF5EC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="eyebrow mb-5">From Our Ongoing Projects</p>
              <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#1C110C] leading-[1.05]">
                Ongoing <em className="text-[#6E1F2C] not-italic">projects</em>.
              </h2>
              <p className="mt-4 max-w-xl text-sm md:text-base text-[#3E2A22] leading-[1.8]">
                Six ongoing projects — selected design visualizations, not
                completed-site photography.
              </p>
            </div>
            <Link
              to="/ongoing-projects"
              data-testid="home-ongoing-view-all"
              className="inline-flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] border-b border-[#6E1F2C] pb-2 hover:text-[#56151F] whitespace-nowrap self-start md:self-auto"
            >
              View All Six <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
            {FEATURED_ONGOING.map((p) => (
              <Link
                key={p.slug}
                to={`/ongoing-projects/${p.slug}`}
                data-testid={`home-ongoing-${p.slug}`}
                className="group block"
              >
                <div className="relative overflow-hidden bg-[#EDE1C8]">
                  <img
                    src={p.images[0].src}
                    loading="lazy"
                    alt={p.images[0].alt}
                    className="w-full aspect-[16/11] object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                  />
                  <span className="absolute top-3 left-3 bg-[#6E1F2C] text-[#FAF5EC] text-[9px] tracking-[0.2em] uppercase px-2.5 py-1">
                    Ongoing project
                  </span>
                  <span className="absolute bottom-3 left-3 bg-[#1C110C]/80 text-[#EDE1C8] text-[9px] tracking-[0.2em] uppercase px-2.5 py-1">
                    Design visualization
                  </span>
                </div>
                <p className="mt-4 text-[9px] tracking-[0.26em] uppercase text-[#6E1F2C]">
                  {p.location ? `${p.category} · ${p.location}` : p.category}
                </p>
                <p className="mt-1.5 font-serif-display text-xl md:text-2xl text-[#1C110C] leading-snug group-hover:text-[#6E1F2C] transition-colors">
                  {p.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 7 · GOOGLE REVIEW PROOF (verified rating only) ========== */}
      <section data-testid="home-review-proof" className="bg-[#FAF5EC]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <p className="eyebrow mb-6">Client Feedback</p>
          <div className="flex items-center justify-center gap-1.5 mb-5" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={22} className="text-[#D4A963]" fill={i < 4 ? "currentColor" : "none"} />
            ))}
          </div>
          <p data-testid="home-review-rating" className="font-serif-display text-4xl md:text-5xl text-[#1C110C]">
            4.3 <span className="text-[#7A6B5F] text-2xl md:text-3xl">/ 5</span>
          </p>
          <p className="mt-4 max-w-xl mx-auto text-[#3E2A22] leading-relaxed">
            Rated 4.3 / 5 on Google for interior projects across the UAE.
          </p>
          <a
            href="https://maps.google.com/maps?cid=17721573559901945835"
            target="_blank"
            rel="noreferrer"
            data-testid="home-review-cta"
            className="mt-10 inline-flex items-center gap-3 border border-[#6E1F2C] text-[#6E1F2C] px-10 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-all"
          >
            <Star size={14} fill="currentColor" /> Read our Google reviews
          </a>
        </div>
      </section>

      {/* ========== 10 · FAQ (AI-search ready · unique queries only) ========== */}
      <FAQSection testid="home-faq" />

      {/* ========== 10 · FINAL CTA ========== */}
      <CTASection
        testid="cta-final"
        eyebrow="Let's Build Your Space"
        heading="Let's Build Your Space."
        subtext="From design to execution, we handle every detail."
      />
    </div>
  );
}

/* ---------- Reusable components ---------- */

function PrimaryCTA({ children, href, to, testid, external }) {
  const cls =
    "group inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-11 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#56151F] transition-all shadow-[0_20px_40px_-10px_rgba(110,31,44,0.6)]";
  if (external)
    return (
      <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className={cls}>
        {children}
      </a>
    );
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {children}
    </Link>
  );
}

function CTASection({ heading, subtext, eyebrow, testid }) {
  return (
    <section data-testid={testid} className="bg-[#FAF5EC]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <p className="eyebrow mb-6">{eyebrow}</p>
        <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-3xl mx-auto text-[#1C110C]">
          {heading}
        </h2>
        <p className="mt-8 max-w-xl mx-auto text-[#3E2A22] leading-relaxed text-lg">{subtext}</p>
        <div className="mt-12 flex flex-wrap gap-5 justify-center">
          <PrimaryCTA testid={`${testid}-book`} to="/contact">
            Book a Site Visit <ArrowRight size={16} />
          </PrimaryCTA>
          <a
            href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappFitOutText}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`${testid}-whatsapp`}
            className="inline-flex items-center gap-3 border border-[#6E1F2C]/60 text-[#6E1F2C] px-11 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#6E1F2C] hover:text-[#EDE1C8] transition-all"
          >
            <MessageCircle size={16} strokeWidth={2.2} /> WhatsApp Now
          </a>
          <a
            href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
            data-testid={`${testid}-call`}
            className="inline-flex items-center gap-3 border border-[#6E1F2C]/60 text-[#6E1F2C] px-11 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#6E1F2C] hover:text-[#EDE1C8] transition-all"
          >
            <Phone size={16} strokeWidth={2.2} /> Call Us
          </a>
        </div>
        <p className="mt-8 text-sm text-[#7A6B5F]">
          Not ready to talk yet?{" "}
          <Link to="/fit-out-cost-calculator-dubai" data-testid="cta-final-planner" className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]">
            Try the Fit-Out Budget Planner
          </Link>{" "}
          for an instant indicative band.
        </p>
      </div>
    </section>
  );
}
